import { create } from "zustand"
import {
    AuthData, autologin, CompanyData, login, logout,
    retrieveCompanies, retrieveComProfile, retrieveLandingIcon,
    retrieveSessionDetails, SessionDetails, setCompany, setCompanyAuth,
    updateDetails, uploadAvatar, uploadComAvatar, verifyDeviceOtpApi,
    // MFA API functions
    getMfaSettingsApi, updateMfaSettingsApi, setMpinApi, MfaSettingsResponse,
    ApiSuccessResponse, ApiErrorResponse, // Import new types
    reauthenticate,
    completeLoginMfaApi,
    AuthResponse
} from "./actions"
import { checkRoles } from "../../utils/functions"
import { clearLastCompany, getLastCompany } from "../../utils/lastCompany"
import { beginSessionSwap, endSessionSwap } from "../sessionSwap"
import { ADMIN_ROLE, CORE1_COM, ERR_DEVICE_ADD, ERR_MFA_REQUIRED, ERR_SESSION_EXPIRED, OWNER_ROLE, SUPPORT_ROLE } from "../../constants"
import { forgotPassword, updatePassword, validateForgot } from "./forgotActions"
import config from "../../config/environment"

export type UserAuth = {
    display: string
    expire: number
}

type MFASettings = {
    avatar?: string;
    display?: string;
    isMfaEnabled?: boolean;
    isPinEnabled: boolean;
    isBiometricsEnabled: boolean;
    isPasskeyEnabled: boolean;
    hasPinSetup: boolean;
    hasPasskeySetup: boolean;
} | null

// Holds the pending second-factor challenge after a successful password login when MFA is on.
export type LoginMfaState = {
    token: string;
    methods: string[];
    username: string;
    display: string;
} | null

type AuthProps = {
    sessionExpired: boolean,
    signingIn: boolean                  // currently signing in?
    autologging: boolean                // is autologin?
    profile: CompanyData | null         // company profile data
    client: string                          // client currently in use for signing
    redir: string                        // redirection url after login
    currentCompany: string
    companies: CompanyData[] | null
    // true while the last selected company is being restored on page load. the shell
    // holds rendering until this clears so pages don't fetch against the wrong company.
    restoringCompany: boolean
    companyRestored: boolean            // restore already attempted for this page load
    signed: boolean
    deviceOtpRef: string
    featuresActive: { [key: string]: boolean | undefined } // use for menus
    landingIcon: string | null
    sessionDetails: SessionDetails | null
    deviceVerificationNeeded: boolean // New state for device verification
    // MFA Settings State
    mfaSettings: MFASettings
    mfaSettingsLoading: boolean;
    mfaSettingsError: string;
    // Pending second-factor challenge after a password login (when MFA is enabled)
    loginMfa: LoginMfaState;
    login: (username: string, pass: string, redir: string) => Promise<AuthData>
    // load or initialize app, also initiates autologin
    loadSSO: (client: string, redir: string) => Promise<AuthData | null>
    setCurrentCompany: (company: string, code: string) => Promise<string>
    autoLogin: (redir: string) => Promise<AuthData | null>
    // this retrieves session details
    loadSession: () => Promise<string>
    signout: (all?: boolean) => Promise<string>
    toggleFeature: (featureId: string, on: boolean) => void
    // is logged use has role
    hasRole: (role: string) => boolean
    setSessionDetails: (sessionDetails: SessionDetails) => void
    uploadAvatar: (img: File) => Promise<string>
    updateUser: (dis: string, lname: string, fname: string) => Promise<string>
    retrieveSessionCom: () => Promise<string> // retrieve user's companies
    setSessionCom: (com: string) => Promise<string> // sets current session company
    // makes the last explicitly selected company active again. runs once per page load
    restoreLastCompany: () => Promise<void>
    setRestoringCompany: (restoring: boolean) => void
    uploadComAvatar: (img: File) => Promise<string>
    retrieveCompanyProfile: () => Promise<string>
    forgotPassword(email: string): Promise<string>
    validateForgot(code: string): Promise<any>
    resetPassword(code: string, username: string, password: string): Promise<string>
    // New device verification actions
    setDeviceVerificationNeeded: (needed: boolean) => void;
    verifyDeviceOtp: (otp: string) => Promise<string>;
    setSessionExpired: (expired: boolean, mfaSettings?: MFASettings) => void; // New state for session expiration
    // MFA Settings Actions
    fetchMfaSettings: () => Promise<void>;
    updateMfaToggles: (payload: { enable_mfa?: boolean; enable_pin?: boolean; enable_passkey?: boolean; password: string }) => Promise<ApiSuccessResponse<string> | ApiErrorResponse>;
    setupMpin: (pin: string, password: string) => Promise<ApiSuccessResponse<string> | ApiErrorResponse>;
    reauth: (credentialValue: string, credentialType: string) => Promise<any>;
    // Completes a paused login by submitting the second factor (mpin or passkey assertion)
    completeLoginMfa: (credentialType: string, credentialValue: string) => Promise<AuthData>;
    // Cancels the pending second-factor challenge and returns to the login form
    clearLoginMfa: () => void;
}

// use to toggle specific features
const checkFeatures = (roles: string[], com: string): { [key: string]: boolean | undefined } => {
    const result = { users: false, dev: false, admin: false, "company-devices": false, "company-ip-whitelist": false };

    const isPrivileged = checkRoles(roles, [OWNER_ROLE, ADMIN_ROLE]);

    result.users = isPrivileged;
    result["company-devices"] = isPrivileged;
    result["company-ip-whitelist"] = isPrivileged;

    if (com === CORE1_COM) {
        result.admin = isPrivileged;
        result.dev = isPrivileged;
    }
    return result;
}

export const decodeJWT = (token: string) => {

    const arrytk = token.split(".")
    const payload = JSON.parse(atob(arrytk[1]))
    ///console.log(payload)
    return payload
}

export const useAuth = create<AuthProps>((set, getState) => ({
    signingIn: false,
    deviceOtpRef: "",
    signed: false,
    retriesLeft: -1,
    profile: null,
    autologging: false,
    sessionDetails: null,
    currentCompany: "",
    companies: null,
    restoringCompany: true,
    companyRestored: false,
    client: "",
    redir: "",
    featuresActive: { users: false, dev: false, admin: false },
    landingIcon: null,
    deviceVerificationNeeded: false, // Initialize new state
    sessionExpired: false, // Initialize new state
    // MFA Settings Initial State
    mfaSettings: null,
    mfaSettingsLoading: false,
    mfaSettingsError: "",
    loginMfa: null,

    async login(username, pass, redir) {
        set(state => ({ signingIn: true }))
        var client = getState().client
        if (client === "") {
            client = config.CLIENT_ID
        }
        const res = await login(client, username, pass, redir)
        if (res.Code === "200") {
            const msg = res.Msg as AuthResponse
            set(state => ({ autologging: false, signed: true, companies: msg.companies, loginMfa: null }))
        }
        else if (res.Code === ERR_MFA_REQUIRED) {
            // Password accepted; require a second factor before completing the login.
            const msg = res.Msg as { token: string; methods: string[]; uname: string; dis: string }
            set(state => ({
                autologging: false,
                signed: false,
                loginMfa: { token: msg.token, methods: msg.methods || [], username: msg.uname || username, display: msg.dis || "" }
            }))
        }
        else if (res.Code === ERR_DEVICE_ADD) {
            const msg = res.Msg as AuthResponse
            set(state => ({ autologging: false, signed: false, deviceVerificationNeeded: true, deviceOtpRef: msg.otpref }))
        }
        else
            set(state => ({ autologging: false, signed: false }))
        set(state => ({ signingIn: false }))
        return res
    },
    async completeLoginMfa(credentialType, credentialValue) {
        set(state => ({ signingIn: true }))
        try {
            const token = getState().loginMfa?.token || ""
            const res = await completeLoginMfaApi(token, credentialType, credentialValue)
            if (res.Code === "200") {
                const msg = res.Msg as AuthResponse
                set(state => ({ autologging: false, signed: true, companies: msg.companies, loginMfa: null }))
            }
            else if (res.Code === ERR_DEVICE_ADD) {
                const msg = res.Msg as AuthResponse
                set(state => ({ autologging: false, signed: false, deviceVerificationNeeded: true, deviceOtpRef: msg.otpref, loginMfa: null }))
            }
            return res
        } catch (error) {
            console.error("Login MFA error:", error)
            return { Code: "NETWORK_ERROR", Msg: "Network error during MFA verification." } as unknown as AuthData
        } finally {
            set(state => ({ signingIn: false }))
        }
    },
    async reauth(credentialValue, credentialType) {
        set(state => ({ signingIn: true }))
        try {
            const res = await reauthenticate(getState().redir, getState().client, credentialValue, credentialType);
            if (res.Code === "200") {
                set(state => ({ sessionExpired: false, autologging: false, signed: true, companies: res.Msg.companies }))
            } else if (res.Code === ERR_DEVICE_ADD) {
                //console.log(res)
                set(state => ({ autologging: false, signed: false, deviceVerificationNeeded: true, deviceOtpRef: res.Msg.otpref }))
            } else {
                set(state => ({ autologging: false, signed: false }))
            }
            return res;
        } catch (error) {
            console.error("Reauthentication error:", error, getState().redir, getState().client, credentialValue, credentialType);
            return { Code: "NETWORK_ERROR", Msg: "Network error during reauthentication." };
        } finally {
            set(state => ({ signingIn: false }))
        }
    },
    clearLoginMfa() {
        set({ loginMfa: null })
    },
    async loadSSO(client, redir) {
        if (getState().landingIcon !== null) return null

        set(state => ({ client, landingIcon: "", redir: redir }))
        retrieveLandingIcon(client).then(response => {
            if (response!.Code === "200") {
                set(state => ({ landingIcon: response.Msg }))
            } else {
                set(state => ({ landingIcon: "" }))
            }
        })
        const res = await getState().autoLogin(redir)
        return res
    },
    async autoLogin(redir) {
        if (getState().signed || getState().autologging) return null

        set(state => ({ autologging: true }))
        const res = await autologin(getState().client, redir)
        if (res.Code === "200") {
            const msg = res.Msg as AuthResponse
            set(state => ({ autologging: false, signed: true, companies: msg.companies }))
        }
        else if (res.Code === "device_add")
            set(state => ({ autologging: false, signed: false, deviceVerificationNeeded: true }))
        else if (res.Code === ERR_SESSION_EXPIRED) {
            const msg = res.Msg as MfaSettingsResponse
            set(state => ({
                autologging: false,
                signed: false,
                sessionExpired: true,
                mfaSettings: {
                    display: msg.dis,
                    avatar: msg.avatar,
                    isPinEnabled: msg.is_pin_enabled,
                    isBiometricsEnabled: msg.is_biometrics_enabled,
                    hasPinSetup: true,
                    hasPasskeySetup: msg.has_passkey_setup,
                    isPasskeyEnabled: msg.is_passkey_enabled
                }
            }))
        }
        else
            set(state => ({ autologging: false, signed: false }))
        return res
    },
    async setCurrentCompany(company, code) {
        const client = getState().client
        set(state => ({ currentCompany: company }))
        const res = await setCompanyAuth(company, code, client)
        return res
    },
    async loadSession() {
        const res = await retrieveSessionDetails()
        if (res.Code === "200") {
            const roles = res.Msg.roles
            const features = checkFeatures(roles, res.Msg.com)
            set(state => ({ sessionDetails: res.Msg, signed: true, featuresActive: features, currentCompany: res.Msg.com, sessionExpired: false }))
        } else if (res.Code === ERR_SESSION_EXPIRED) {
            const msg = res.Msg as MfaSettingsResponse
            set(state => ({
                autologging: false,
                signed: false,
                sessionExpired: true,
                mfaSettings: {
                    isPinEnabled: msg.is_pin_enabled,
                    isBiometricsEnabled: msg.is_biometrics_enabled,
                    hasPinSetup: true,
                    hasPasskeySetup: msg.has_passkey_setup,
                    isPasskeyEnabled: msg.is_passkey_enabled
                }
            }))
        } else {
            set(state => ({ signed: false, sessionExpired: false }))
        }
        return res.Code
    },
    async signout(all?: boolean) {
        //console.log("Signing out...",getState())
        if (!getState().signed && !getState().sessionExpired) return "loading"
        const res = await logout(all)
        set({ signed: false })
        return res
    },
    toggleFeature(featureId, on) {
        set(state => ({ ...state, [featureId]: on }))
    },
    hasRole(role) {
        if (!getState().sessionDetails?.roles) return false;
        for (const item of getState().sessionDetails?.roles!) {
            if (item === role)
                return true
        }
        return false
    },
    setSessionDetails(sessionDetails) {
        set(state => ({ sessionDetails: sessionDetails }))
        updateDetails(sessionDetails.dis, sessionDetails.lname, sessionDetails.fname)
    },
    async uploadAvatar(img) {
        const res = await uploadAvatar(img)
        if (res.Code === "200") {
            const details = { ...getState().sessionDetails }
            details.avatar = res.Msg
            set(state => ({ sessionDetails: details as SessionDetails }))
        }
        return res.Code
    },
    async updateUser(dis, lname, fname) {
        return updateDetails(dis, lname, fname)
    },
    async retrieveSessionCom() {
        const res = await retrieveCompanies()
        if (res.Code === "200") {
            set(state => ({ companies: res.Msg.companies }))
        }
        return res.Code
    },
    async setSessionCom(com) {
        set(state => ({ currentCompany: com }))
        // the backend replaces the session on a company switch and terminates the old one.
        // suppress session expiry handling until the new session is settled, otherwise a
        // request racing the swap raises the re-authentication prompt
        beginSessionSwap()
        try {
            const code = await setCompany(com)
            if (code === "200") {
                // settle the new session before returning so callers never render stale company data
                await getState().loadSession()
            }
            await getState().retrieveCompanyProfile()
            return code
        } finally {
            endSessionSwap()
        }
    },
    setRestoringCompany(restoring) {
        set(state => ({ restoringCompany: restoring }))
    },
    async restoreLastCompany() {
        if (getState().companyRestored) return
        set(state => ({ companyRestored: true }))
        try {
            const last = getLastCompany()
            const current = getState().sessionDetails
            // nothing remembered, no session yet, or already on that company
            if (last === "" || current === null || last === current.com) return

            // make sure the user still belongs to the remembered company. a different
            // user on this browser, or revoked access, must fall back to the session company
            if (getState().companies === null) await getState().retrieveSessionCom()
            const companies = getState().companies
            if (!companies || !companies.some(com => com.id === last)) {
                clearLastCompany()
                return
            }

            const code = await getState().setSessionCom(last)
            if (code !== "200") {
                // blocked by ip whitelist / device approval, or access removed.
                // setSessionCom sets currentCompany optimistically, so roll it back
                clearLastCompany()
                set(state => ({ currentCompany: current.com }))
            }
        } catch (err) {
            console.error("Failed to restore last company:", err)
        } finally {
            set(state => ({ restoringCompany: false }))
        }
    },
    async retrieveCompanyProfile() {
        const res = await retrieveComProfile()
        if (res.Code === "200") {
            const com = { ...res.Msg.com, ...res.Msg }
            set(state => ({ profile: com }))
        }
        return res.Code
    },
    async uploadComAvatar(img) {
        const res = await uploadComAvatar(img)
        if (res.Code === "200") {
            const details = { ...getState().profile! }
            details.img = res.Msg
            set(state => ({ profile: details }))
        }
        return res.Code
    },
    async forgotPassword(email: string) {
        const res = await forgotPassword(email);
        return res;
    },
    async validateForgot(code: string) {
        const res = await validateForgot(code);
        return res;
    },
    async resetPassword(code: string, username: string, pass: string) {
        const res = await updatePassword(code, username, pass);
        return res;
    },
    setDeviceVerificationNeeded: (needed: boolean) => {
        set(state => ({ deviceVerificationNeeded: needed }));
    },
    async verifyDeviceOtp(otp: string): Promise<string> {
        console.log("Attempting to verify OTP:", otp);
        const refid = getState().deviceOtpRef; // Get refid from store state
        try {
            const res = await verifyDeviceOtpApi(refid, otp);
            return res; // Return the response data (which includes the Code)
        } catch (error) {
            console.error("Error verifying OTP:", error);
            // If verifyDeviceOtpApi rejects (e.g., network error), return a generic error
            return "NETWORK_ERROR"
        }
    },
    setSessionExpired: (expired: boolean, mfaSettings?: MFASettings) => {
        set((state) => ({ ...state, sessionExpired: expired, mfaSettings: mfaSettings }));
    },
    // MFA Settings Action Implementations
    async fetchMfaSettings() {
        set({ mfaSettingsLoading: true, mfaSettingsError: "" });
        try {
            const response = await getMfaSettingsApi();
            if (response.Code === 200) {
                // When Code is "200", Msg is MfaSettingsResponse
                const apiMsg = response.Msg as MfaSettingsResponse;
                set({
                    mfaSettings: {
                        isMfaEnabled: apiMsg.is_mfa_enabled,
                        isPinEnabled: apiMsg.is_pin_enabled,
                        isBiometricsEnabled: apiMsg.is_biometrics_enabled,
                        hasPinSetup: apiMsg.has_pin_setup,
                        hasPasskeySetup: apiMsg.has_passkey_setup,
                        isPasskeyEnabled: apiMsg.is_passkey_enabled
                    },
                    mfaSettingsLoading: false
                });
            } else {
                set({ mfaSettingsError: response.Code, mfaSettingsLoading: false });
            }
        } catch (error: any) {
            set({ mfaSettingsError: error.message, mfaSettingsLoading: false });
        }
    },
    async updateMfaToggles(payload) {
        set({ mfaSettingsLoading: true, mfaSettingsError: "" });
        try {
            const response = await updateMfaSettingsApi(payload);
            if (response.Code === 200) {
                await getState().fetchMfaSettings(); // Re-fetch to ensure UI consistency
            } else {
                // If the API call itself returns an error response, we should return it
                // instead of throwing a generic error, to match the return type.
                set({ mfaSettingsError: response.Msg, mfaSettingsLoading: false });
            }
            return response; // Return the API response
        } catch (error: any) {
            const errorResponse: ApiErrorResponse = {
                Code: "NETWORK_ERROR", // Or a more specific error code
                Msg: error.message || "An unexpected error occurred during MFA toggle update.",
            };
            set({ mfaSettingsError: errorResponse.Msg, mfaSettingsLoading: false });
            return errorResponse; // Return an error response
        }
    },
    async setupMpin(pin, password) {
        set({ mfaSettingsLoading: true, mfaSettingsError: "" });
        try {
            const response = await setMpinApi(pin, password);
            if (response.Code === 200) {
                await getState().fetchMfaSettings(); // Re-fetch to update hasPinSetup etc.
            } else {
                // If the API call itself returns an error response, we should return it
                // instead of throwing a generic error, to match the return type.
                set({ mfaSettingsLoading: false });
            }
            return response; // Return the API response
        } catch (error: any) {
            const errorResponse: ApiErrorResponse = {
                Code: "NETWORK_ERROR", // Or a more specific error code
                Msg: error.message || "An unexpected error occurred during MPIN setup.",
            };
            set({ mfaSettingsError: errorResponse.Msg, mfaSettingsLoading: false });
            return errorResponse; // Return an error response
        }
    },
})); // Added a comment to trigger re-evaluation
